import clsx from 'clsx'
import { type MouseEvent, useState } from 'react'

import { FvButton, FvLinkButton, Icon } from '@fv/client-components'
import type { ListShipment } from '@fv/client-types'
import { fvDate, fvDateUtc } from '@fv/dates'

import { List, ListItem } from '@/components/List'
import { StopList } from '@/components/shared/StopList'
import { useBolLinkState } from '@/hooks/shipments/useBolLinkState'
import { getApplicableAccessorials } from '@/utils/locationFuncs'

import { usePrefetchTracking } from '../track/queries'
import { TrackHistoryList } from '../track/TrackHistoryList'
import ShipmentCardHeader from './ShipmentCardHeader'
import { ShipmentDetailRow } from './ShipmentDetailRow'
import type { SelectableShipmentProps } from './types'

type Props = Partial<SelectableShipmentProps> & {
  isActive: boolean
  onClick: () => void
  shipment: ListShipment
}

const getDateDifference = (targetDate?: string) => {
  if (!targetDate) return { expired: true }
  const now = fvDate()
  const expiryDate = fvDate(targetDate)

  return {
    duration: expiryDate.fromNow(),
    expired: expiryDate.isBefore(now),
  }
}

export const ShipmentCard = ({
  isActive,
  onClick,
  shipment,
  selected,
  setSelected,
}: Props) => {
  const {
    equipment,
    loadId,
    locations,
    selectedQuote,
    status,
    tracking,
    workflow,
    bidExpiration,
  } = shipment

  const dateDiff = getDateDifference(
    bidExpiration?.utc ? fvDateUtc(bidExpiration?.utc).toString() : undefined,
  )
  const [isExpanded, setExpanded] = useState(false)
  const prefetchTracking = usePrefetchTracking()
  const {
    fvBolUrl,
    hideFvBolFromSpotQuote,
    isProcessing: isBolProcessing,
  } = useBolLinkState(shipment)
  const inProcess = status === 'pending' || status === 'awarded'
  const hasTracking = !inProcess && !!tracking?.currentLocation?.summary
  const isExpandable = Boolean(
    hasTracking ||
      !!equipment.accessorials?.length ||
      locations.find(
        x =>
          !!getApplicableAccessorials(x, workflow)?.length ||
          !!x.refNums?.length,
      ),
  )

  return (
    <div
      className={clsx(
        'shipment-card rounded-tr-lg after:absolute after:-left-3 after:top-0 after:bottom-0 after:w-0.5',
        {
          'after:bg-fv-blue-300': status === 'confirmed',
          'after:bg-fv-green': status === 'delivered',
          'after:bg-fv-beer-medium': status === 'picked-up',
          'after:bg-[#ff9999]': status === 'canceled',
        },
        status,
        isActive
          ? 'shipment-card--active border-fv-orange border-2'
          : 'border-fv-gray border mx-px',
      )}
      data-loadid={shipment.loadId} // for scroll to on list type toggle
      onClick={e => {
        onClick()
        scrollIntoPosition(e)
      }}
    >
      <ShipmentCardHeader
        shipment={shipment}
        selected={selected}
        setSelected={setSelected}
      />

      <div className="shipment-card__content relative">
        {inProcess && dateDiff.duration && (
          <div
            className={clsx(
              'bg-fv-beer-light border-fv-gray absolute right-0 top-0 border-b border-l px-2 py-1 text-xs',
              isActive && '-right-px border-r z-50',
            )}
          >
            <Icon icon="clock" className="-mr-[.2rem] text-fv-orange-bright" />
            <span>
              {' '}
              {dateDiff?.expired ? 'Bidding expired' : 'Bidding expires'}{' '}
              {dateDiff?.duration}{' '}
            </span>
          </div>
        )}
        {isExpandable && (
          <FvButton
            className="shipment-card__view-more-btn"
            theme="plain"
            icon="arrows-from-line"
            onClick={() => setExpanded(s => !s)}
            onMouseEnter={() => {
              if (!hasTracking) return
              prefetchTracking(loadId)
            }}
          />
        )}

        {status !== 'pending' && !hideFvBolFromSpotQuote && fvBolUrl && (
          <FvLinkButton
            rel="noreferrer"
            target="_blank"
            href={fvBolUrl}
            className="shipment-card__bol-link"
            icon={isBolProcessing ? 'sync' : 'file'}
          >
            FV BOL
          </FvLinkButton>
        )}

        <List fcp0 lcp0>
          <ShipmentDetailRow isExpanded={isExpanded} shipment={shipment} />

          <StopList
            isExpanded={isExpanded}
            locations={locations}
            tracking={tracking}
            workflow={workflow}
            quote={selectedQuote}
          />

          {hasTracking && (
            <ListItem className="flex items-start">
              <TrackHistoryList load={shipment} isCollapsed={!isExpanded} />
            </ListItem>
          )}
        </List>
        {isActive && (
          <div className="absolute -top-[2px] -bottom-[2px] -right-[calc(1.5rem+2px)] bg-white w-[calc(1.5rem_+2px)] z-40 border-fv-orange border-2 border-l-0 border-r-0" />
        )}
      </div>
    </div>
  )
}

function scrollIntoPosition(
  e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
) {
  const shipmentCard = e.currentTarget
  const shipmentList = document.getElementById('shipmentList')

  if (
    !shipmentCard ||
    !shipmentList ||
    shipmentCard.className.includes('--active')
  ) {
    return
  }

  // Get position of card relative to top of list container viewPort
  const shipmentCardRelativePos = shipmentCard.getBoundingClientRect()

  // Get position of 1st card (used to set scroll distance)
  const firstShipmentCard =
    document.querySelector<HTMLDivElement>('.shipment-card')
  const firstCardStart = firstShipmentCard?.offsetTop ?? 0

  // Get height of list with overflow sans padding
  const styles = window.getComputedStyle(shipmentList)
  const shipmentListContentHeight =
    shipmentList.scrollHeight - parseFloat(styles.paddingBottom)

  // Get height of visible list container (without overflow)
  const shipmentListContainerHeight = shipmentList.clientHeight

  // Get start position of card relative to entire list
  const shipCardOffset = e.currentTarget.offsetTop

  // To prevent a card from being clicked and not scrolling to the top because
  // there is not enough content below it, add required padding to move card
  // to the same position as others that have enough scrollable real estate.
  // else, set list container back to default padding
  if (
    shipCardOffset >
    shipmentListContentHeight - shipmentListContainerHeight
  ) {
    const bottomPadding =
      shipmentListContainerHeight - shipmentCardRelativePos.top + 'px'

    shipmentList.style.paddingBottom = bottomPadding
  } else {
    shipmentList.style.paddingBottom = ''
  }

  const rootFontSize = parseFloat(
    getComputedStyle(document.documentElement).fontSize,
  )

  shipmentList.scroll({
    top: shipCardOffset - firstCardStart - SCROLL_OFFSET_REM * rootFontSize,
    behavior: 'smooth',
  })
}

const SCROLL_OFFSET_REM = -0.4
